<template>
  <StandardLayout>
    <v-sheet class="mx-12 my-6" rounded slot="main">
      <h1 class="outfit-regular mb-3">Realm Dashboard</h1>

      <v-sheet v-if="profile.username" class="d-flex pa-6 mb-6 align-center" color="yellow lighten-5">
        <v-avatar size="120" tile class="mr-4">
          <v-img :src="profile.avatar"></v-img>
        </v-avatar>
        <!-- <div class="font-size-20">Welcome back, <strong class="yellow--text text--darken-3">{{ profile.username }}</strong>!</div> -->
        <div>Welcome back, {{ profile.username }}!</div>
      </v-sheet>

      <v-tabs v-model="tab">
        <v-tabs-slider :color="['blue', 'green', 'purple', 'yellow darken-3'][tab]"></v-tabs-slider>
        <v-tab color="yellow">Your Games</v-tab>
        <v-tab color="yellow">Your Stories</v-tab>
        <v-tab color="blue">Your Lessons</v-tab>
        <v-tab color="yellow">Your Ideas</v-tab>
      </v-tabs>
      <v-tabs-items v-model="tab">
        <!-- ------------ Games ------------ -->
        <v-tab-item>
          <v-progress-linear v-if="loading.games" indeterminate color="blue"></v-progress-linear>
          <v-list three-line v-if="!loading.games && games.length > 0" class="transparent-list">
            <v-list-item v-for="(game, index) in games" :key="game.id" link @click="go('GamePlayer', game.id)" class="game-item" color="transparent">
              <v-list-item-avatar tile size="80" color="grey lighten-3">
                <v-img v-if="game.thumbnail" :src="game.thumbnail"></v-img>
                <v-icon v-else>mdi-gamepad-variant</v-icon>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title class="cal-sans-regular font-size-20">{{ game.title || 'Untitled Game' }}</v-list-item-title>
                <v-list-item-subtitle>
                  Status: <v-chip small :color="game.state === 'public' ? 'green' : 'orange'" dark>{{ getGameStatus(game.state) }}</v-chip>
                  - Last updated: {{ game.updatedAt ? new Date(game.updatedAt.seconds * 1000).toLocaleDateString() : 'N/A' }}
                </v-list-item-subtitle>
              </v-list-item-content>
              <!-- <v-list-item-action class="align-self-center">
                <v-btn icon>
                  <v-icon color="grey lighten-1">mdi-chevron-right</v-icon>
                </v-btn>
              </v-list-item-action> -->
              <v-list-item-action class="align-self-center">
                <div class="d-flex flex-row align-center">
                  <v-btn x-small fab elevation="0" @click="editItem('game', game.id)" color="grey lighten-1">
                    <v-icon>mdi-pencil</v-icon>
                  </v-btn>
                  <v-btn icon @click="go('GamePlayer', game.id)" color="grey lighten-1">
                    <v-icon>mdi-chevron-right</v-icon>
                  </v-btn>
                </div>
              </v-list-item-action>
            </v-list-item>
          </v-list>
          <v-alert v-if="!loading.games && games.length === 0" type="info" outlined color="blue" class="mt-6">
            <div class="d-flex flex-row align-center">
              <div class="flex-1 black--text">You haven't created any games yet.</div>
              <v-btn @click="createItem('game')" color="blue" dark>
                <v-icon left>mdi-creation-outline</v-icon>
                Create Game
              </v-btn>
            </div>
          </v-alert>
        </v-tab-item>
        <!-- ------------ Stories ------------ -->
        <v-tab-item>
          <v-list three-line v-if="!loading.stories && stories.length > 0">
            <v-list-item v-for="(story, index) in stories" :key="story.id">
              <v-list-item-avatar tile size="80" color="grey lighten-3">
                <v-img v-if="story.thumbnail" :src="story.thumbnail"></v-img>
                <v-icon v-else>mdi-book-open-variant</v-icon>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title class="cal-sans-regular font-size-20">{{ story.title || story.name || 'Untitled Story' }}</v-list-item-title>
                <v-list-item-subtitle>
                  Status: <v-chip small :color="story.state === 'public' ? 'green' : 'orange'" dark>{{ getGameStatus(story.state) }}</v-chip>
                  - Last updated: {{ story.updatedAt ? new Date(story.updatedAt.seconds * 1000).toLocaleDateString() : 'N/A' }}
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action class="align-self-center">
                <div class="d-flex flex-column">
                  <v-btn icon @click="editItem('story', story.id)" color="primary">
                    <v-icon>mdi-pencil</v-icon>
                  </v-btn>
                  <v-btn icon @click="go('StoryPlayer', story.id)" color="grey lighten-1">
                    <v-icon>mdi-play</v-icon>
                  </v-btn>
                </div>
              </v-list-item-action>
            </v-list-item>
          </v-list>
          <v-alert v-if="!loading.stories && stories.length === 0" type="info" outlined color="green" class="mt-6">
            <div class="d-flex flex-row align-center">
              <div class="flex-1 black--text">You haven't created any stories yet.</div>
              <v-btn @click="createItem('story')" color="green" dark>
                <v-icon left>mdi-creation-outline</v-icon>
                Create a Story
              </v-btn>
            </div>
          </v-alert>
        </v-tab-item>
        <!-- ------------ Lessons ------------ -->
        <v-tab-item>
          <v-list three-line>
            <div v-for="(lesson, index) in lessons" :key="lesson.id">
              <v-list-item link @click="go('LessonPlayer', lesson.id)">
                <v-list-item-title v-html="lesson.title"></v-list-item-title>
              </v-list-item>
            </div>
          </v-list>
          <v-alert v-if="!loading.lessons && lessons.length === 0" type="info" outlined color="purple" class="mt-6">
            <div class="d-flex flex-row align-center">
              <div class="flex-1 black--text">You haven't created any lessons yet.</div>
              <v-btn @click="createItem('lesson')" color="purple" dark>
                <v-icon left>mdi-creation-outline</v-icon>
                Create a Lesson
              </v-btn>
            </div>
          </v-alert>
        </v-tab-item>
        <!-- ------------ Ideas ------------ -->
        <v-tab-item>
          <v-list three-line>
            <div v-for="(idea, index) in ideas" :key="idea.id">
              <v-list-item link @click="go('IdeaPlayer', idea.id)">
                <v-list-item-title v-html="idea.title"></v-list-item-title>
              </v-list-item>
            </div>
          </v-list>
          <v-alert v-if="!loading.ideas && ideas.length === 0" type="info" outlined color="orange" class="mt-6">
            <div class="d-flex flex-row align-center">
              <div class="flex-1 black--text">You haven't created any ideas yet.</div>
              <v-btn @click="createItem('idea')" color="orange" dark>
                <v-icon left>mdi-creation-outline</v-icon>
                Create an Idea
              </v-btn>
            </div>
          </v-alert>
        </v-tab-item>
      </v-tabs-items>

      <CharacterSelector v-if="displayCharacterSelector" />

      <v-divider class="my-6"></v-divider>

      <v-dialog v-model="deleteObject.dialog" max-width="490">
        <v-card>
          <v-card-title class="headline">Delete {{ deleteObject.title }}</v-card-title>
          <v-card-text>Are you sure you want to delete this?</v-card-text>
          <v-card-actions>
            <v-btn @click="deleteObject.dialog = false" text>Keep it</v-btn>
            <v-spacer></v-spacer>
            <v-btn @click="confirmDelete()" color="red" dark>Delete it!</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-sheet>
  </StandardLayout>
</template>

<script>
import CharacterSelector from '@/components/CharacterSelector.vue'; // Verify path
import { db } from '@/firebase/init';
import { doc, getDoc, addDoc, collection, query, where, getDocs, updateDoc } from 'firebase/firestore'
import StandardLayout from '@/layouts/Standard.vue'

export default {
  
  name: 'RealmDashboard',
  
  components: {
    CharacterSelector,
    StandardLayout,
  },
  
  data() {
    return {
      displayCharacterSelector: false,
      tab: 'games',
      loading: {
        games: false,
        stories: false,
        lessons: false,
        ideas: false,
      },
      gamesLoading: false,
      userGames: [],
      deleteObject: {
        id: null,
        dialog: false,
        title: null,
        collection: null,
      },
      items: [
        {
          avatar: 'https://cdn.vuetifyjs.com/images/lists/1.jpg',
          title: 'Brunch this weekend?',
          subtitle: `<span class="text--primary">Ali Connors</span> &mdash; I'll be in your neighborhood doing errands this weekend. Do you want to hang out?`,
        },
        { divider: true, inset: true },
        {
          avatar: 'https://cdn.vuetifyjs.com/images/lists/2.jpg',
          title: 'Summer BBQ <span class="grey--text text--lighten-1">4</span>',
          subtitle: `<span class="text--primary">to Alex, Scott, Jennifer</span> &mdash; Wish I could come, but I'm out of town this weekend.`,
        },
        { divider: true, inset: true },
        {
          avatar: 'https://cdn.vuetifyjs.com/images/lists/3.jpg',
          title: 'Oui oui',
          subtitle: '<span class="text--primary">Sandra Adams</span> &mdash; Do you have Paris recommendations? Have you ever been?',
        },
        { divider: true, inset: true },
        {
          avatar: 'https://cdn.vuetifyjs.com/images/lists/4.jpg',
          title: 'Birthday gift',
          subtitle: '<span class="text--primary">Trevor Hansen</span> &mdash; Have any ideas about what we should get Heidi for her birthday?',
        },
        { divider: true, inset: true },
        {
          avatar: 'https://cdn.vuetifyjs.com/images/lists/5.jpg',
          title: 'Recipe to try',
          subtitle: '<span class="text--primary">Britta Holt</span> &mdash; We should eat this: Grate, Squash, Corn, and tomatillo Tacos.',
        },
      ],
      games: [],
      stories: [],
      lessons: [],
      ideas: [],
    };
  },

  async mounted() {
    this.displayCharacterSelector = this.profile && !this.profile.username;
    await this.fetchGames();
    await this.fetchStories();
    await this.fetchLessons();
    await this.fetchIdeas();
    await this.fetchUserGames();
  },

  methods: {
    go(routeName, id) {
      if (id) {
        this.$router.push({ name: routeName, params: { id } });
      } else {
        this.$router.push({ name: routeName });
      }
    },

    async createItem(type) {
      await this.$creator.create(type, this.profile);
    },

    editItem(type, id) {
      this.$creator.edit(type, id);
    },

    getGameStatus(state) {
      const statusMap = {
        'public': 'Published',
        'draft': 'Draft',
        'private': 'Private'
      };
      return statusMap[state] || state;
    },
    
    async fetchGames() {
      this.loading.games = true;
      const gamesRef = collection(db, 'games');
      const q = query(gamesRef, where('creator.id', '==', this.authUser.uid));
      const querySnapshot = await getDocs(q);
      let games = [];
      querySnapshot.docs.forEach(doc => {
        let game = doc.data();
        game.id = doc.id;
        games.push(game);
      });
      this.games = games;
      this.loading.games = false;
    },

    async fetchUserGames() {
      this.gamesLoading = true;
      try {
        if (this.authUser && this.authUser.uid) {
          const gamesRef = collection(db, 'games');
          const q = query(gamesRef, where('creator.id', '==', this.authUser.uid));
          const querySnapshot = await getDocs(q);
          let games = [];
          querySnapshot.docs.forEach(doc => {
            let game = doc.data();
            game.id = doc.id;
            games.push(game);
          });
          this.userGames = games;
        } else {
          this.userGames = [];
        }
      } catch (error) {
        console.error("Error fetching user games:", error);
        this.userGames = [];
      } finally {
        this.gamesLoading = false;
      }
    },

    async fetchStories() {
      this.loading.stories = true;
      const storiesRef = collection(db, 'stories');
      const q = query(storiesRef, where('creator.id', '==', this.authUser.uid));
      const querySnapshot = await getDocs(q);
      let stories = [];
      querySnapshot.docs.forEach(doc => {
        let story = doc.data();
        story.id = doc.id;
        stories.push(story);
      });
      this.stories = stories;
      this.loading.stories = false;
    },

    async fetchLessons() {
      this.loading.lessons = true;
      const lessonsRef = collection(db, 'lessons');
      const q = query(lessonsRef, where('creator.id', '==', this.authUser.uid));
      const querySnapshot = await getDocs(q);
      let lessons = [];
      querySnapshot.docs.forEach(doc => {
        let lesson = doc.data();
        lesson.id = doc.id;
        lessons.push(lesson);
      });
      this.lessons = lessons;
      this.loading.lessons = false;
    },

    async fetchIdeas() {
      this.loading.ideas = true;
      const ideasRef = collection(db, 'ideas');
      const q = query(ideasRef, where('creator.id', '==', this.authUser.uid));
      const querySnapshot = await getDocs(q);
      let ideas = [];
      querySnapshot.docs.forEach(doc => {
        let idea = doc.data();
        idea.id = doc.id;
        ideas.push(idea);
      });
      this.ideas = ideas;
      this.loading.ideas = false;
    },

    deleteCreation(id, title, collection) {
      this.deleteObject = {
        id: id,
        dialog: true,
        title: title,
        collection: collection,
      }
    },
    async confirmDelete() {
      // Instead of deleting, we set the state to archived
      await updateDoc(doc(db, this.deleteObject.collection, this.deleteObject.id), {
        state: 'archived'
      }).then(() => {
        // cut from 'collection' array
        this[this.deleteObject.collection] = this[this.deleteObject.collection].filter(item => item.id !== this.deleteObject.id);
        // reset delete object
        this.deleteObject = {
          id: null,
          dialog: false,
          title: null,
          collection: null,
        }
      });
      // snackbar
      this.$snackbarStore.showSnackbar('Creation deleted', 'red', 3000);
    },
    
  },

  watch: {
    profile(newVal, oldVal) { // Added oldVal for more precise check
      if (newVal && newVal.username) { // Check if newVal exists before accessing username
        this.displayCharacterSelector = false;
      }
      // Fetch games if profile is loaded or profile.id becomes available
      if (newVal && newVal.id && (!oldVal || newVal.id !== oldVal.id)) {
        this.fetchUserGames();
      }
    }
  }
  
};
</script>

<style scoped>
  .transparent-list {
    background-color: transparent !important;
  }
  
  .game-item:hover {
    background-color: #ecf5fe !important;
  }
  
  /* Apply to all v-list-item elements inside transparent-list */
  .transparent-list .v-list-item {
    background-color: transparent !important;
  }
</style>